/**
 * Newsletter-Anmeldung (Startseite 13 + EN-Übersetzung /en/): fängt das Absenden des Kadence-Formulars ab (Capture-Phase, vor dem
 * Kadence-Handler) und schickt die Anmeldung an n8n, das das Brevo-Double-Opt-in auslöst.
 * Nachfolger von js/pc-newsletter.js + js/pc-newsletter-service.js; Texte aus src/i18n (newsletter.*).
 * Seit 26.10.2026 mit `sprache` (de/en): n8n trägt EN in Brevo-Liste 6 mit EN-DOI-Vorlage ein, DE wie bisher in Liste 5.
 * @module components/forms/newsletter-form
 */
import { getPageLocale } from '../../shared/services/config.js';
import { t } from '../../shared/utils/i18n.js';
import { EMAIL_PATTERN, postForm } from './form.service.js';

const FORM_SELECTOR = '.pc-newsletter .kb-form';

/**
 * Anmeldung senden; Erfolg nur bei Antwort { ok: true }.
 * @param {{email: string, consent: boolean, sprache: string, website: string}} data Daten
 * @returns {Promise<object>} Antwort
 */
async function subscribe(data) {
  const response = await postForm('newsletter', data);
  if (!response.ok) throw new Error(`Server error ${response.status}`);
  const result = await response.json().catch(() => null);
  if (!result || result.ok !== true) throw new Error('Anmeldung nicht bestaetigt');
  return result;
}

/**
 * Meldung unter dem Formular.
 * @param {HTMLFormElement} form Formular
 * @param {'success'|'error'} type Art
 * @param {string} text Text
 * @returns {void}
 */
function showMessage(form, type, text) {
  let box = form.parentNode.querySelector('.pc-newsletter__message');
  if (!box) {
    box = document.createElement('div');
    box.setAttribute('role', 'status');
    form.parentNode.appendChild(box);
  }
  box.className = `pc-newsletter__message pc-newsletter__message--${type}`;
  box.textContent = text;
}

/**
 * Submit-Handler (Capture-Phase am document).
 * @param {SubmitEvent} event Ereignis
 * @returns {Promise<void>}
 */
async function onSubmit(event) {
  const form = event.target && event.target.closest ? event.target.closest(FORM_SELECTOR) : null;
  if (!form) return;
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const field = (name) => form.querySelector(`[name="${name}"]`);
  const emailField = field('kb_field_0');
  const consentField = field('kb_field_1');
  const honeypot = field('_kb_verify_email');
  const button = form.querySelector('.kb-forms-submit');

  const email = emailField ? emailField.value.trim() : '';
  if (!EMAIL_PATTERN.test(email)) {
    showMessage(form, 'error', t('newsletter.invalidEmail'));
    return;
  }
  if (!consentField || !consentField.checked) {
    showMessage(form, 'error', t('newsletter.noConsent'));
    return;
  }
  if (button) button.disabled = true;
  try {
    await subscribe({
      email,
      consent: true,
      sprache: getPageLocale().language === 'en' ? 'en' : 'de',
      website: honeypot ? honeypot.value : '',
    });
    showMessage(form, 'success', t('newsletter.success'));
    form.reset();
  } catch {
    showMessage(form, 'error', t('newsletter.error'));
  } finally {
    if (button) button.disabled = false;
  }
}

/**
 * Bindet die Newsletter-Anmeldung (einmal pro Seite).
 * @returns {void}
 */
export function initNewsletterForm() {
  document.addEventListener('submit', onSubmit, true);
}
